import React, { useEffect, useState } from "react";
import { Download, FileText, TrendingUp, ReceiptIndianRupee, ShoppingBag, Percent, Wallet, Truck, Package, AlertTriangle, Eye } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { PageHeader, LoadingBlock, StatCard, EmptyState, StatusBadge } from "../../components/app/ui";
import { useMyRestaurant } from "./RestaurantOrders";
import RestaurantProfileForm from "../../components/app/RestaurantProfileForm";
import ReportFilters, { defaultFilter, toParams } from "../../components/app/ReportFilters";
import { restaurantApi, inr2, errorMessage } from "../../lib/api";
import { RestaurantOrderDialog } from "../../components/app/HistoryDetailDialog";

export default function SalesReport() {
  const [restaurant, setRestaurant] = useMyRestaurant();
  const [filter, setFilter] = useState(defaultFilter);
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState("");
  const [viewOrderId, setViewOrderId] = useState(null);

  useEffect(() => {
    if (restaurant) { setData(null); restaurantApi.sales(toParams(filter)).then(setData).catch(() => setData(null)); }
  }, [restaurant, filter]);

  const download = async (format) => {
    setDownloading(format);
    try { await restaurantApi.downloadSales(toParams(filter), format); toast.success(`${format.toUpperCase()} downloaded`); }
    catch (e) { toast.error(errorMessage(e, "Download failed")); } finally { setDownloading(""); }
  };

  if (restaurant === undefined) return <><PageHeader title="Sales report" /><LoadingBlock rows={3} /></>;
  if (restaurant === null) return <><PageHeader title="Set up your restaurant" description="Create your restaurant profile first." /><div className="max-w-3xl"><RestaurantProfileForm onSaved={setRestaurant} title="Create your restaurant" /></div></>;

  const s = data?.summary;
  const maxRev = Math.max(1, ...(data?.by_day || []).map((d) => d.revenue));

  return (
    <>
      <PageHeader title="Sales report" description={`Revenue, fees and payouts${data?.label ? ` · ${data.label}` : ""}.`} actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => download("csv")} disabled={!!downloading} className="rounded-full" data-testid="download-csv"><Download className="mr-2 h-4 w-4" /> CSV</Button>
          <Button variant="outline" onClick={() => download("pdf")} disabled={!!downloading} className="rounded-full" data-testid="download-pdf"><FileText className="mr-2 h-4 w-4" /> PDF</Button>
        </div>
      } />

      <ReportFilters value={filter} onChange={setFilter} />

      {data === null && <LoadingBlock rows={3} />}
      {s && (
        <>
          <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard label="Gross revenue" value={inr2(s.gross_revenue)} hint={`${s.delivered} delivered`} icon={ReceiptIndianRupee} tone="brand" />
            <StatCard label="Net sales" value={inr2(s.net_sales)} hint={`${s.orders} orders · ${s.cancelled} cancelled`} icon={TrendingUp} tone="graphite" />
            <StatCard label="Platform commission" value={inr2(s.commission)} hint={`${s.commission_gst ? `+ ${inr2(s.commission_gst)} GST (${s.commission_gst_pct}%)` : "13% of net sales"}`} icon={Percent} tone="ink" data-testid="sales-commission" />
            <StatCard label="Net payout" value={inr2(s.net_payout)} hint="After commission + its GST + packaging − penalty" icon={Wallet} tone="brand" />
          </div>
          <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard label={`GST on commission (${s.commission_gst_pct || 18}%)`} value={inr2(s.commission_gst || 0)} hint="On commission only — remitted to govt" icon={Percent} tone="ink" data-testid="sales-commission-gst" />
            <StatCard label="Packaging fees" value={inr2(s.packaging_fees || 0)} hint="₹7/item — you keep 100%" icon={Package} tone="brand" data-testid="sales-packaging" />
            <StatCard label="Total penalty" value={inr2(s.penalties || 0)} hint="Rejected / missed orders (₹100 each)" icon={AlertTriangle} tone="ink" data-testid="sales-penalty" />
            <StatCard label="Delivery fees (to drivers)" value={inr2(s.delivery_fees || 0)} hint="Collected on orders, paid to delivery partners" icon={Truck} tone="graphite" />
            <StatCard label="Orders" value={s.orders} hint={`${s.cancelled} cancelled`} icon={ShoppingBag} />
            <StatCard label="Avg order value" value={inr2(s.avg_order_value)} icon={Percent} tone="graphite" />
            <StatCard label="Discounts" value={inr2(s.discounts)} icon={Percent} />
          </div>
          <div className="kt-card p-5" data-testid="sales-by-day">
            <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-ink-muted">Daily revenue</h2>
            {(data.by_day || []).length === 0 ? <EmptyState icon={TrendingUp} title="No sales in this period" text="Delivered orders will show up here." /> : (
              <div className="space-y-2">
                {data.by_day.map((d) => (
                  <div key={d.date} className="flex items-center gap-3 text-sm" data-testid={`day-${d.date}`}>
                    <span className="w-24 shrink-0 text-ink-soft">{d.date.slice(5)}</span>
                    <div className="h-6 flex-1 overflow-hidden rounded-lg bg-surface-100"><div className="flex h-full items-center justify-end rounded-lg bg-brand px-2 text-[10px] font-semibold text-white" style={{ width: `${Math.max(8, (d.revenue / maxRev) * 100)}%` }}>{inr2(d.revenue)}</div></div>
                    <span className="w-16 shrink-0 text-right text-xs text-ink-muted">{d.orders} ord</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="kt-card mt-6 p-5" data-testid="sales-orders">
            <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-ink-muted">All orders {data.orders?.length ? `(${data.orders.length})` : ""}</h2>
            {(data.orders || []).length === 0 ? <EmptyState icon={ShoppingBag} title="No orders in this period" text="Orders will appear here once customers order." /> : (
              <div className="-mx-2 overflow-x-auto">
                <table className="w-full min-w-[720px] border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-surface-300 text-left text-xs uppercase tracking-wider text-ink-muted">
                      <th className="px-2 py-2 font-semibold">Order</th>
                      <th className="px-2 py-2 font-semibold">Date</th>
                      <th className="px-2 py-2 font-semibold">Status</th>
                      <th className="px-2 py-2 font-semibold">Customer</th>
                      <th className="px-2 py-2 text-right font-semibold">Items</th>
                      <th className="px-2 py-2 text-right font-semibold">Subtotal</th>
                      <th className="px-2 py-2 text-right font-semibold">Delivery fee</th>
                      <th className="px-2 py-2 text-right font-semibold">Total</th>
                      <th className="px-2 py-2 font-semibold">Payment</th>
                      <th className="px-2 py-2 text-right font-semibold">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.orders.map((o) => (
                      <tr key={o.order_id} className="border-b border-surface-200 last:border-0" data-testid={`sales-order-${o.order_id}`}>
                        <td className="px-2 py-2 font-medium text-ink">#{o.order_id.slice(-6).toUpperCase()}</td>
                        <td className="px-2 py-2 text-ink-soft">{new Date(o.created_at).toLocaleDateString()}</td>
                        <td className="px-2 py-2"><StatusBadge status={o.status} /></td>
                        <td className="px-2 py-2 text-ink-soft">{o.customer_name || "—"}</td>
                        <td className="px-2 py-2 text-right text-ink-soft">{o.items_count}</td>
                        <td className="px-2 py-2 text-right text-ink-soft">{inr2(o.subtotal)}</td>
                        <td className="px-2 py-2 text-right text-ink-soft">{inr2(o.delivery_fee)}</td>
                        <td className="px-2 py-2 text-right font-semibold text-ink">{inr2(o.total)}</td>
                        <td className="px-2 py-2 text-xs text-ink-muted">{o.payment_method === "razorpay" ? "Online" : "Cash"}{o.payment_status === "refunded" ? " · refunded" : ""}</td>
                        <td className="px-2 py-2 text-right"><Button variant="outline" size="sm" className="rounded-full" onClick={() => setViewOrderId(o.order_id)} data-testid={`view-order-${o.order_id}`}><Eye className="mr-1 h-3.5 w-3.5" /> View</Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
      <RestaurantOrderDialog orderId={viewOrderId} fetcher={restaurantApi.orderDetail} onClose={() => setViewOrderId(null)} />
    </>
  );
}
